import { HttpClient } from "@angular/common/http";
import { Injectable, OnDestroy } from "@angular/core";
import { AccessControlService } from "app/access-control/access-control-service";
import { hasAccess } from "app/access-control/permission-verification";
import { PatientProfile } from "app/access-control/permissions";
import { Events, Platform, ToastController } from "ionic-angular";
import { PatientResponseDto } from "models/scriptSure";
import moment from "moment";
import { FeatureToggleDataService } from "providers/features-service/feature-toggle.data.service";
import { MedicationDataService } from "providers/order-data-service/medications-data-service";
import { BehaviorSubject, Subject, Subscription } from "rxjs";
import { Observable } from "rxjs/Observable";
import { of } from "rxjs/observable/of";
import {
  distinctUntilChanged,
  filter,
  finalize,
  mergeMap,
  shareReplay,
} from "rxjs/operators";
import * as Enums from "../../app/access-control/access-control-constant";
import {
  PATIENT_CHART_PAGE,
  PATIENT_NAVIGATION_BAR_PERMISSIONS,
  PATIENT_REFERRAL_PAGE,
} from "../../app/access-control/common-permission-asks";
import PatientChart from "../../models/patient-chart/patientChartResponse";
import { commonVar } from "../common-var/common";
import { ConfigurationService } from "../configuration/configuration.service";
import { PatientChartStatusServiceProvider } from "../patient-charts/patient-chart-status-service";
import { ReferralsServiceProvider } from "../patient-charts/referrals-service";
import { StateManagementService } from "../state-management/state-management.service";

interface MRNChange {
  mrn: string;
  forceRefresh?: boolean;
}

@Injectable()
export class NavBarServiceProvider implements OnDestroy {
  private patientChartDataCache = new Map<string, Observable<object>>();
  static PATIENT_CHART_FEATURE_TOGGLE_NAME: string = "Patient Chart"; // New Patient Chart
  static HIS_FEATURE_TOGGLE_NAME: string = "HIS"; // HOSPICE ITEM SET (Essentially what we know as reports)
  static CTI_FEATURE_TOGGLE_NAME: string = "CTI"; // Certifications
  static CTI_V2_FEATURE_TOGGLE_NAME: string = "CTI_v2"; // Certifications
  static PALLIATIVE_FEATURE_TOGGLE_NAME: string = "Palliative Care";
  static HOSPICE_BILLING_FEATURE_TOGGLE_NAME: string = "Hospice Billing";
  static HOSPICE_HOUSE_FEATURE_TOGGLE_NAME: string = "Hospice House";
  static ADVERSE_EVENT_TOGGLE_NAME: string = "Adverse Event";
  static CREDENTIAL_TOGGLE_NAME: string = "Credentials";
  static BENEFITS_TOGGLE_NAME: string = "Patient Benefit";
  static BENEFITS_REPORT_STATUS_TOGGLE_NAME: string =
    "REPORT - Benefit Status Report";
  static ELIGIBILITY_TOGGLE_NAME: string = "Eligibility";
  static PRN_TOGGLE_NAME: string = "PRN Visit";
  static REPORT_MANAGEMENT_TOGGLE_NAME: string = "Billing Reports";
  static AR_SUMMARY_TOGGLE_NAME: string = "AR Details Details";
  static AR_DETAILS_TOGGLE_NAME: string = "AR Details Summary";
  static MONTHLY_REVENUE_SUMMARY_TOGGLE_NAME: string =
    "Monthly Revenue Summary";
  static PRE_BILLING_REPORTS_TOGGLE_NAME: string = "Pre-Billing Reports";
  static LEVEL_OF_CARE_SUMMARY_TOGGLE_NAME: string = "Level Of Care Summary";
  static LEVEL_OF_CARE_DETAILS_TOGGLE_NAME: string = "Level Of Care Details";
  static PATIENT_MEDICATIONS_TOGGLE_NAME: string = "Patient Medications";
  static PATIENT_ALLERGIES_TOGGLE_NAME: string = "Patient Allergies";
  static ORDER_ENTRY_OM_TOGGLE_NAME: string = "Order Entry/OM";
  static HIS_V2_EVAL_CARRYOVER_TEMP: string = "HIS_v2 eval carryover temp";
  static MONTH_END_CLOSE_TOGGLE_NAME: string = "Month End Close";
  static ADMIT_WITHOUT_CTI_CHECK_TOGGLE_NAME: string = "CTI_notRequiredToAdmit";
  static ADMIT_ACTION_NAME: string = "admit";
  static ADMIT_WITHOUT_CTI_CHECK_ACTION_NAME: string = "admit_sans_cti";
  static E_PRESCRIBE: string = "ePrescribe";
  static HIS_EXPORTED_TOGGLE_NAME: string = "HISExportV1";

  static BEREAVEMENT_DISCHARGE_REASON = "expired"; // Discharge reason to show bereavement tab

  private readonly patientChartDataUrl: string;
  private readonly idgDataUrl: string;
  private readonly accessControlUrl: string;

  private qBosId: any;
  public externalId: number;
  private patientChartId: number;
  private patientChart: PatientChart;
  patientAgeEmpty: boolean;

  private careType: string;
  private admitDate: string;

  private patientChartStatus: string;
  private patientChartStatusSubscription: Subscription;
  private tokenRefreshSubscription: Subscription;
  public featureToggleData;

  public accessControlData: any = [];
  private accessControlPageName: string[] = PATIENT_CHART_PAGE;

  private statusSubscription: Subscription;

  private dischargeReason: string;

  public hospiceFeatureToggle: boolean;
  private hospiceFeatureToggleChange: Subject<boolean> = new Subject<boolean>();
  private reportManagementFeatureToggleChange: Subject<boolean> =
    new Subject<boolean>();
  private arSummaryFeatureToggleChange: Subject<boolean> =
    new Subject<boolean>();
  private arDetailsFeatureToggleChange: Subject<boolean> =
    new Subject<boolean>();
  private monthlyRevenueSummaryFeatureToggleChange: Subject<boolean> =
    new Subject<boolean>();
  private preBillingReportsFeatureToggleChange: Subject<boolean> =
    new Subject<boolean>();
  private levelOfCareSummaryFeatureToggleChange: Subject<boolean> =
    new Subject<boolean>();
  private levelOfCareDetailsFeatureToggleChange: Subject<boolean> =
    new Subject<boolean>();
  private inFlightAdmitCheckPatientId: string | null = null;

  public adverseEventFeatureToggle: boolean;
  private adverseEventFeatureToggleChange: Subject<boolean> =
    new Subject<boolean>();

  public benefitsFeatureToggle: boolean;
  private benefitsFeatureToggleChange: Subject<boolean> =
    new Subject<boolean>();

  public eligibilityFeatureToggle: boolean;
  private eligibilityFeatureToggleChange: Subject<boolean> =
    new Subject<boolean>();

  public monthEndCloseToggle: boolean;
  private monthEndCloseToggleChange: Subject<boolean> = new Subject<boolean>();
  public cti_notRequiredToAdmitToggle: boolean;
  public cti_notRequiredToAdmitToggleChange: Subject<boolean> =
    new Subject<boolean>();

  private scriptSurePatientSubject =
    new BehaviorSubject<PatientResponseDto | null>(null);

  private mrnSubject: Subject<MRNChange>;

  private apiUrl: string;

  constructor(
    public httpClient: HttpClient,
    public events: Events,
    public stateData: StateManagementService,
    private toastCtrl: ToastController,
    private referralServiceProvider: ReferralsServiceProvider,
    public patientChartStatusService: PatientChartStatusServiceProvider,
    private config: ConfigurationService,
    private featureToggleDataService: FeatureToggleDataService,
    private platform: Platform,
    private medicationDataService: MedicationDataService,
    private accessCtrlData: AccessControlService
  ) {
    this.idgDataUrl = config.resolveIdgUrl();
    this.accessControlUrl = config.getConfig().apiUrl;
    this.apiUrl = config.getConfig().apiUrl;

    this.patientChartDataUrl = this.config.resolvePatientChartsUrl();

    this.events.subscribe("checkAdmitStatus", () => {
      this.checkAdmitStatus();
    });

    this.events.subscribe("admitStatusChange", () => {
      this.checkAdmitStatus();
      this.events.publish("reloadNavBarService");
    });

    this.events.subscribe("patientChartIdChange", (patientChartInfo) => {
      this.initializePatientData(patientChartInfo);
    });

    this.hospiceFeatureToggleChange.subscribe((value) => {
      this.hospiceFeatureToggle = value;
    });

    this.adverseEventFeatureToggleChange.subscribe((value) => {
      this.adverseEventFeatureToggle = value;
    });

    this.benefitsFeatureToggleChange.subscribe((value) => {
      this.benefitsFeatureToggle = value;
    });

    this.eligibilityFeatureToggleChange.subscribe((value) => {
      this.eligibilityFeatureToggle = value;
    });

    this.monthEndCloseToggleChange.subscribe((value) => {
      this.monthEndCloseToggle = value;
    });

    this.cti_notRequiredToAdmitToggleChange.subscribe((value) => {
      this.cti_notRequiredToAdmitToggle = value;
    });

    this.initFeatureToggleData();
    //this.initAccessControlData();????????????
    this.events.subscribe("user:ReloadToggles", () => {
      this.initFeatureToggleData();
    });

    this.patientChartStatusSubscription = this.stateData
      .getPatientChartStatusObserver()
      .subscribe((chartStatus: string) => {
        this.patientChartStatus = chartStatus;
      });
    // Subscribe to token refresh
    this.tokenRefreshSubscription = this.stateData
      .getTokenRefreshedSubject()
      .pipe(filter((token) => token !== null))
      .subscribe(() => {
        this.events.publish("patientChartIdChange");
      });
  }

  subscribeToReloadNavBar(callback: () => void) {
    this.events.subscribe("reloadNavBarService", () => {
      callback();
    });
  }

  public initFeatureToggleData(): Observable<any> {
    this.featureToggleData = null;
    return new Observable((observer) => {
      this.loadFeatureToggleData().subscribe(
        (featureToggleResponseData) => {
          if (featureToggleResponseData) {
            this.setFeatureToggleData(featureToggleResponseData);
            this.setHospiceFeatureToggle();
            this.setReportManagementFeatureToggle();
            this.setArSummaryFeatureToggle();
            this.setArDetailsFeatureToggle();
            this.setMonthlyRevenueSummaryFeatureToggle();
            this.setPreBillingReportsFeatureToggle();
            this.setLevelOfCareSummaryFeatureToggle();
            this.setLevelOfCareDetailsFeatureToggle();
            this.setAdverseEventFeatureToggle();
            this.setBenefitsFeatureToggle();
            this.setEligibilityFeatureToggle();
            this.setMonthEndCloseFeatureToggle();
            this.setCTIV2NotRequiredToggle();
            this.setScriptSurePatient(this.patientChart);
            this.events.publish("reloadNavBar");
            this.events.publish(
              "featureToggleLoaded",
              featureToggleResponseData
            );
            observer.next(featureToggleResponseData);
            observer.complete();
            this.logInfo(
              "loadFeatureToggleData",
              {
                arg: "NONE",
                pageName: this.accessControlPageName,
                where: "Constructor",
              },
              featureToggleResponseData
            );
          }
        },
        (error) => {
          this.logError(
            "loadFeatureToggleData",
            {
              arg: "NONE",
              pageName: this.accessControlPageName,
              where: "Constructor",
            },
            error
          );
          observer.error(error);
          observer.complete();
        }
      );
    });
  }

  public initializePatientData(patientChartInfo: any = undefined) {
    if (
      this.getPatientChartStatus() &&
      (this.getPatientChartStatus().toLowerCase() ===
        PatientChart.PATIENT_CHART_STATUS_REFERRAL ||
        this.getPatientChartStatus().toLowerCase() ===
          PatientChart.PATIENT_CHART_STATUS_EVALUATION)
    ) {
      this.setAccessControlPageName(PATIENT_REFERRAL_PAGE);
    }

    if (!patientChartInfo || !patientChartInfo.patientChartId) {
      let session = JSON.parse(
        window.sessionStorage.getItem("patientChartInfo")
      );
      const urlHash = patientChartInfo?.urlHash
        ? patientChartInfo.urlHash
        : window.location.hash;
      patientChartInfo = session
        ? session
        : this.parsePatientChartIdsFromUrl(urlHash);
    }

    if (!patientChartInfo || !patientChartInfo.patientChartId) {
      return;
    }

    // Immediately update in-memory IDs from the event data so that
    // getExternalId()/getPatientChartId() return current values even
    // before the async API call resolves (e.g. when user clicks
    // HIS/HOPE sidebar right after navigating to a new patient).
    this.externalId = Number(patientChartInfo.id) || this.externalId;
    if (!patientChartInfo.externalId) {
      // externalId flag is false → patientChartId is the internal ID
      this.patientChartId =
        Number(patientChartInfo.patientChartId) || this.patientChartId;
    }

    if (
      this.patientChart &&
      this.patientChart.id === +patientChartInfo.patientChartId
    ) {
      // Defensive resync: heal partially-cleared sessionStorage on cache hit
      // without losing the early-return performance optimization. Inline-write
      // the 4 keys (no event publish — would re-enter this method).
      const externalIdToWrite =
        this.externalId || patientChartInfo.id || this.patientChart.externalId;
      const internalIdToWrite = this.patientChart.id;
      if (externalIdToWrite && internalIdToWrite) {
        const resyncedInfo = {
          id: externalIdToWrite,
          externalId: false,
          patientChartId: internalIdToWrite,
        };
        window.sessionStorage.setItem(
          "patientChartInfo",
          JSON.stringify(resyncedInfo)
        );
        window.sessionStorage.setItem("externalId", String(externalIdToWrite));
        window.sessionStorage.setItem(
          "patientChartId",
          String(internalIdToWrite)
        );
        window.sessionStorage.setItem("patChartId", String(internalIdToWrite));
      }
      return;
    } else {
      this.patientChart = undefined;
    }

    function processData(patientChartResponseData) {
      if (patientChartResponseData) {
        this.setPatientChartData(patientChartResponseData);
        this.setPatientChartId(patientChartResponseData);
        this.setExternalId(patientChartResponseData);
        this.setPatientChartStatus(patientChartResponseData);
        this.setDischargeReason(patientChartResponseData);
        this.setScriptSurePatient(patientChartResponseData);
        this.patientAgeEmpty = patientChartResponseData.patient.age == -1;
        // Sync all sessionStorage keys with the resolved internal/external IDs.
        // The popstate handler may have written patientChartId = externalId
        // before this async response returned the correct internal ID.
        const resolvedInternal = patientChartResponseData.id;
        const resolvedExternal =
          patientChartResponseData.externalId || resolvedInternal;
        const updatedInfo = {
          id: resolvedExternal,
          externalId: false,
          patientChartId: resolvedInternal,
        };
        window.sessionStorage.setItem(
          "patientChartInfo",
          JSON.stringify(updatedInfo)
        );
        window.sessionStorage.setItem("patChartId", String(resolvedInternal));
        this.checkAdmitStatus();
        this.events.publish("reloadNavBar");
        this.events.publish("reloadBriefBarEvent", patientChartResponseData);
      }
    }
    // Use the externalId flag from patientChartInfo to determine the correct query.
    // patientChartInfo.externalId is a boolean: true = patientChartId is an external ID,
    // false = patientChartId is an internal ID.
    const useExternalId = patientChartInfo.externalId === true;
    this.getNewPatientChartData(
      patientChartInfo.patientChartId,
      useExternalId
    ).subscribe(
      (data) => {
        processData.call(this, data);
      },
      (error) => {
        // Don't delete patientChartInfo — it may still have valid IDs for other calls.
        this.setExternalId(patientChartInfo);
        this.logError("getNewPatientChartData", patientChartInfo, error);
        this.presentToast(
          "An error occurred loading patient chart data. " +
            "If problem persist please contact support. NB3"
        );
      }
    );
  }

  private presentToast(message) {
    this.logError("presentToast", { arg: "NONE" }, message);
  }

  public patientChartFeatureToggleOn(): boolean {
    return this.featureToggleOn(
      NavBarServiceProvider.PATIENT_CHART_FEATURE_TOGGLE_NAME
    );
  }

  public hisFeatureToggleOn(): boolean {
    return this.featureToggleOn(NavBarServiceProvider.HIS_FEATURE_TOGGLE_NAME);
  }

  public palliativeFeatureToggleOn(): boolean {
    return this.featureToggleOn(
      NavBarServiceProvider.PALLIATIVE_FEATURE_TOGGLE_NAME
    );
  }

  public hospiceBillingFeatureToggleOn(): boolean {
    return this.featureToggleOn(
      NavBarServiceProvider.HOSPICE_BILLING_FEATURE_TOGGLE_NAME
    );
  }

  public certificationFeatureToggleOn(): boolean {
    return this.featureToggleOn(NavBarServiceProvider.CTI_FEATURE_TOGGLE_NAME);
  }

  public certificationV2FeatureToggleOn(): boolean {
    return this.featureToggleOn(
      NavBarServiceProvider.CTI_V2_FEATURE_TOGGLE_NAME
    );
  }

  public credentialFeatureToggleOn(): boolean {
    return this.featureToggleOn(NavBarServiceProvider.CREDENTIAL_TOGGLE_NAME);
  }

  public hospiceHouseFeatureToggleOn(): boolean {
    return this.featureToggleOn(
      NavBarServiceProvider.HOSPICE_HOUSE_FEATURE_TOGGLE_NAME
    );
  }

  public adverseEventFeatureToggleOn(): boolean {
    return this.featureToggleOn(
      NavBarServiceProvider.ADVERSE_EVENT_TOGGLE_NAME
    );
  }

  public benefitsFeatureToggleOn(): boolean {
    return this.featureToggleOn(NavBarServiceProvider.BENEFITS_TOGGLE_NAME);
  }
  public benefitsDetailsByPayerFeatureToggleOn(): boolean {
    return this.featureToggleOn(
      NavBarServiceProvider.BENEFITS_REPORT_STATUS_TOGGLE_NAME
    );
  }

  public patientMedicationsFeatureToggleOn(): boolean {
    return this.featureToggleOn(
      NavBarServiceProvider.PATIENT_MEDICATIONS_TOGGLE_NAME
    );
  }
  public patientEPrescribeFeatureToggleOn(): boolean {
    return this.featureToggleOn(NavBarServiceProvider.E_PRESCRIBE);
  }

  public orderEntryOMFeatureToggleOn(): boolean {
    return this.featureToggleOn(
      NavBarServiceProvider.ORDER_ENTRY_OM_TOGGLE_NAME
    );
  }
  public patientAllergiesFeatureToggleOn(): boolean {
    return this.featureToggleOn(
      NavBarServiceProvider.PATIENT_ALLERGIES_TOGGLE_NAME
    );
  }

  public eligibilityFeatureToggleOn(): boolean {
    return this.featureToggleOn(NavBarServiceProvider.ELIGIBILITY_TOGGLE_NAME);
  }

  public prnFeatureToggleOn(): boolean {
    return this.featureToggleOn(NavBarServiceProvider.PRN_TOGGLE_NAME);
  }

  public reportManagementFeatureToggleOn(): boolean {
    return this.featureToggleOn(
      NavBarServiceProvider.REPORT_MANAGEMENT_TOGGLE_NAME
    );
  }

  public arSummaryFeatureToggleOn(): boolean {
    return this.featureToggleOn(NavBarServiceProvider.AR_SUMMARY_TOGGLE_NAME);
  }

  public arDetailsFeatureToggleOn(): boolean {
    return this.featureToggleOn(NavBarServiceProvider.AR_DETAILS_TOGGLE_NAME);
  }

  public monthlyRevenueSummaryFeatureToggleOn(): boolean {
    return this.featureToggleOn(
      NavBarServiceProvider.MONTHLY_REVENUE_SUMMARY_TOGGLE_NAME
    );
  }

  public preBillingReportsFeatureToggleOn(): boolean {
    return this.featureToggleOn(
      NavBarServiceProvider.PRE_BILLING_REPORTS_TOGGLE_NAME
    );
  }

  public levelOfCareSummaryFeatureToggleOn(): boolean {
    return this.featureToggleOn(
      NavBarServiceProvider.LEVEL_OF_CARE_SUMMARY_TOGGLE_NAME
    );
  }

  public levelOfCareDetailsFeatureToggleOn(): boolean {
    return this.featureToggleOn(
      NavBarServiceProvider.LEVEL_OF_CARE_DETAILS_TOGGLE_NAME
    );
  }

  public hisV2EvalCarryoverTempToggleOn(): boolean {
    return this.featureToggleOn(
      NavBarServiceProvider.HIS_V2_EVAL_CARRYOVER_TEMP
    );
  }

  public monthEndCloseFeatureToggleOn(): boolean {
    return this.featureToggleOn(
      NavBarServiceProvider.MONTH_END_CLOSE_TOGGLE_NAME
    );
  }

  public cti_notRequiredToAdmitToggleOn(): boolean {
    return this.featureToggleOn(
      NavBarServiceProvider.ADMIT_WITHOUT_CTI_CHECK_TOGGLE_NAME
    );
  }

  public hisExportedFeatureToggleOn(): boolean {
    return this.featureToggleOn(NavBarServiceProvider.HIS_EXPORTED_TOGGLE_NAME);
  }

  private setPatientChartStatus(patientChartResponseData: PatientChart) {
    this.patientChartStatus = patientChartResponseData.patientStatus;
  }

  public getPatientChartStatus() {
    return this.patientChartStatus;
  }

  private setQBosId(qBosId: number) {
    this.qBosId = qBosId;
    this.externalId = qBosId;
  }

  private setPatientChartData(patientChartResponseData: PatientChart) {
    if (
      patientChartResponseData.patient &&
      patientChartResponseData.patient.id
    ) {
      this.stateData.setPatientId(
        patientChartResponseData.patient.id.toString()
      );
    }
    this.patientChart = patientChartResponseData;
  }

  private setExternalId(patientChartResponseData: PatientChart) {
    if (patientChartResponseData.externalId) {
      this.externalId = patientChartResponseData.externalId;
      this.setQBosId(patientChartResponseData.externalId);
    } else if (patientChartResponseData.id) {
      this.externalId = patientChartResponseData.id;
      this.setQBosId(patientChartResponseData.id);
    } else if (patientChartResponseData["patientChartId"]) {
      this.externalId = patientChartResponseData["patientChartId"];
      this.setQBosId(patientChartResponseData["patientChartId"]);
    }

    window.sessionStorage.setItem("externalId", `${this.externalId}`);
  }

  public getExternalId() {
    // Prefer sessionStorage — it's kept in sync by all code paths
    // (referrals-service, reports-container, popstate handler, processData).
    // The in-memory value can be stale after browser back/forward navigation
    // when the page is restored from Ionic cache without re-triggering
    // initializePatientData.
    return (
      Number(window.sessionStorage.getItem("externalId")) || this.externalId
    );
  }

  private setPatientChartId(patientChartResponseData: PatientChart) {
    this.patientChartId = patientChartResponseData.id;
    this.events.publish("setPatientChartId", this.patientChartId);
    window.sessionStorage.setItem("patientChartId", `${this.patientChartId}`);
  }

  public getPatientChartId() {
    return (
      Number(window.sessionStorage.getItem("patientChartId")) ||
      this.patientChartId
    );
  }

  public getPatientChart(): PatientChart {
    return this.patientChart;
  }

  public parsePatientChartIdsFromUrl(urlHash: string): {
    patientChartId: number;
    externalId: boolean;
  } {
    let patientChart = {
      patientChartId: undefined,
      externalId: false,
    };

    if (urlHash.toString().match(/\d+/)) {
      patientChart.patientChartId = Number(urlHash.toString().match(/\d+/)[0]);
    }

    if (patientChart.patientChartId < 199999) {
      patientChart.externalId = true;
    }

    return patientChart;
  }

  public convertStatusUrlSection(patientStatus: string) {
    if (patientStatus) {
      if (
        patientStatus.toLowerCase() ===
        PatientChart.PATIENT_CHART_STATUS_ON_SERVICE
      ) {
        return "patient";
      }
      if (
        patientStatus.toLowerCase() ===
          PatientChart.PATIENT_CHART_STATUS_DISCHARGED ||
        patientStatus.toLowerCase() ===
          PatientChart.PATIENT_CHART_STATUS_CANCELLED
      ) {
        return "discharge";
      }
      if (
        patientStatus.toLowerCase() ===
        PatientChart.PATIENT_CHART_STATUS_EVALUATION
      ) {
        return "evaluation";
      }
      return patientStatus.toLowerCase();
    }
  }

  // users branch
  // public loadAccessControlData(){}
  // master branch(replace)
  public loadAccessControlData(pageName: string[]) {
    if (pageName != this.accessControlPageName) {
      this.setAccessControlPageName(pageName);
      this.getAccessControlData();
    }
  }

  public getNewPatientChartData(
    patientChartId,
    isExternal
  ): Observable<object> {
    // Resolve the correct ID+flag combination. The caller may pass the external ID
    // with isExternal=false (wrong combo) due to stale in-memory state.
    // Priority: sessionStorage patientChartInfo > in-memory navBarService > caller args.
    let patientChartInfo = JSON.parse(
      window.sessionStorage.getItem("patientChartInfo")
    );
    if (
      patientChartInfo &&
      patientChartInfo.patientChartId &&
      patientChartInfo.patientChartId !== patientChartInfo.id
    ) {
      // We have both IDs and they differ — use the internal ID with externalId=false
      patientChartId = patientChartInfo.patientChartId;
      isExternal = false;
    } else if (patientChartInfo && patientChartInfo.id) {
      patientChartId = patientChartInfo.id;
      isExternal = true;
    } else if (
      this.patientChartId &&
      this.externalId &&
      this.patientChartId !== this.externalId
    ) {
      // sessionStorage gone but in-memory values still available
      patientChartId = this.patientChartId;
      isExternal = false;
    }
    let url =
      this.patientChartDataUrl +
      `/patient-charts/${patientChartId}?externalId=${isExternal}`;
    const cacheKey = `${patientChartId}_${isExternal}`;
    if (this.patientChartDataCache.has(cacheKey)) {
      return this.patientChartDataCache.get(cacheKey);
    }
    const shared$ = new Observable<object>((observer) => {
      this.httpClient.get(url).subscribe(
        (data) => {
          observer.next(data);
          observer.complete();
        },
        (err) => {
          observer.error(err);
          observer.complete();
        }
      );
    }).pipe(
      finalize(() => this.patientChartDataCache.delete(cacheKey)),
      shareReplay(1)
    );
    this.patientChartDataCache.set(cacheKey, shared$);
    return shared$;
  }

  private getPatientChartDataWithQBosId(qBosId: number): Observable<object> {
    let url = `${this.idgDataUrl}/company-resources/patient-charts/${qBosId}?externalId=true`;

    return new Observable((observer) => {
      this.httpClient.get(url).subscribe(
        (data) => {
          observer.next(data);
          observer.complete();
        },
        (err) => {
          observer.error(err);
          observer.complete();
        }
      );
    });
  }

  private loadFeatureToggleData(): Observable<object> {
    return of(this.featureToggleDataService.getAllStoredToggles());
  }

  private getAccessControlData() {
    this.accessCtrlData
      .getPermission(
        PATIENT_NAVIGATION_BAR_PERMISSIONS,
        this.accessControlPageName[0]
      )
      .subscribe((data) => {
        this.setAccessControlData(data);
        hasAccess(PatientProfile.View, data.permissions);
      });
  }

  private setFeatureToggleData(data: object) {
    this.featureToggleData = data;
  }

  // users branch
  // public featureToggleOn(name: string): boolean {
  //     return this.featureToggleDataService.getToggleValue(name);
  // }
  // master branch(replace)
  public featureToggleOn(name: string): boolean {
    if (!this.featureToggleData) {
      //poor man's solution to guarantee feature toggle data being loaded
      this.initFeatureToggleData().subscribe(
        () => {
          return this.checkFeatureToggleOn(name);
        },
        () => {}
      );
    } else {
      return this.checkFeatureToggleOn(name);
    }
  }

  public checkFeatureToggleOn(name: string): boolean {
    let result = false;
    if (this.featureToggleData)
      this.featureToggleData.forEach((element) => {
        if (element.name === name) {
          result = element.active;
        }
      });
    return result;
  }

  private getIconByState(state) {
    if (!state) return commonVar.ADD_ICON;
    return state === "TRUE"
      ? commonVar.COMPLETED_ICON
      : state === "FALSE"
      ? commonVar.ADD_ICON
      : commonVar.IN_PROGRESS_ICON;
  }

  private handleIconsForHospice(
    complianceStates: object,
    profileState: string,
    action: string
  ): void {
    this.referralServiceProvider.PATIENT_PROFILE_SIDEBAR_ICON =
      this.getIconByState(profileState);
    this.referralServiceProvider.CARE_TEAM_SIDEBAR_ICON = this.getIconByState(
      complianceStates["CARE_TEAM"]
    );
    this.referralServiceProvider.BENEFITS_SIDEBAR_ICON = this.getIconByState(
      complianceStates["BENEFITS"]
    );
    this.referralServiceProvider.CONSENTS_ICON = this.getIconByState(
      complianceStates["CONSENT"]
    );
    if (action !== NavBarServiceProvider.ADMIT_WITHOUT_CTI_CHECK_ACTION_NAME) {
      this.referralServiceProvider.CERTIFICATE_ICON = this.getIconByState(
        complianceStates["CERTIFICATION"]
      );
    }
  }

  private checkHospiceProfileState(
    complianceStates: object,
    patientAgeEmpty: boolean
  ): string {
    let compliance = "PARTIAL";
    let checkStates = new Set();
    checkStates.add(complianceStates["PATIENT_CHART_STATUS"]);
    checkStates.add(complianceStates["PATIENT_CHART_CONTACT"]);
    checkStates.add(complianceStates["LEVEL_OF_CARE"]);
    checkStates.add(complianceStates["DIAGNOSIS"]);
    checkStates.add(complianceStates["PATIENT_CHART_DETAILS"]);
    if (checkStates.size == 1) {
      const allStates = Array.from(checkStates)[0];
      if ((allStates === "TRUE" && !patientAgeEmpty) || allStates === "FALSE") {
        compliance = allStates;
      }
    }
    return compliance;
  }

  private checkPalliativeCompliance(
    complianceStates: object,
    eligibilityFeatureToggle: boolean
  ): string {
    let isCompliant = "TRUE";
    let profileState;
    for (let key of Array.from(Object.keys(complianceStates))) {
      let state = complianceStates[key];
      if (state && state !== "TRUE") {
        isCompliant = "FALSE";
      }
    }
    if (
      complianceStates["PATIENT_CHART_STATUS"] === "TRUE" &&
      complianceStates["DIAGNOSIS"] === "TRUE" &&
      this.eligibilityFeatureToggle
    ) {
      profileState = "TRUE";
      isCompliant = "TRUE";
    } else if (
      complianceStates["PATIENT_CHART_STATUS"] === "TRUE" &&
      !this.eligibilityFeatureToggle
    ) {
      profileState = "TRUE";
      isCompliant = "TRUE";
    } else if (
      complianceStates["PATIENT_CHART_STATUS"] === "FALSE" &&
      complianceStates["DIAGNOSIS"] === "FALSE"
    ) {
      profileState = "FALSE";
    } else {
      profileState = "PARTIAL";
    }
    this.referralServiceProvider.PATIENT_PROFILE_SIDEBAR_ICON =
      this.getIconByState(profileState);
    return isCompliant;
  }

  public checkAdmitStatus() {
    const patientChartStatus: string = this.getPatientChartStatus();
    if (
      !patientChartStatus ||
      patientChartStatus.toLowerCase() !==
        PatientChart.PATIENT_CHART_STATUS_REFERRAL
    ) {
      //Load nav-bar for all patient statuses
      this.events.publish("reloadNavBar");
      return;
    }

    const currentPatientId = this.referralServiceProvider.PATIENT_ID;

    // Patient changed while a previous check was in flight: cancel it so
    // a stale response can't re-enable the button for the new patient.
    if (
      this.inFlightAdmitCheckPatientId &&
      this.inFlightAdmitCheckPatientId !== currentPatientId
    ) {
      if (this.statusSubscription) {
        this.statusSubscription.unsubscribe();
      }
      this.inFlightAdmitCheckPatientId = null;
    }

    // Same-patient dedup: a check is already running for this patient.
    if (this.inFlightAdmitCheckPatientId === currentPatientId) {
      return;
    }

    // Disable immediately; an enabled state from a prior patient must not
    // survive navigation until the new patient's spec check returns.
    this.referralServiceProvider.ENABLE_ADMIT = false;
    this.events.publish("reloadNavBar");

    // CARE_TYPE drives the validation path; without it we cannot know which
    // specs are required. Bail and wait for the patient-data load to set it
    // and re-trigger this check (referral-tabs.ts after CARE_TYPE assignment).
    const careType: string = this.referralServiceProvider.CARE_TYPE;
    if (!careType) {
      return;
    }
    const careTypeIsHospice = careType.toLowerCase() === "hospice";
    let action: string;
    if (
      careTypeIsHospice &&
      this.featureToggleOn("CTI_notRequiredToAdmit")
    ) {
      action = NavBarServiceProvider.ADMIT_WITHOUT_CTI_CHECK_ACTION_NAME;
    } else {
      action = NavBarServiceProvider.ADMIT_ACTION_NAME;
    }

    this.inFlightAdmitCheckPatientId = currentPatientId;
    this.statusSubscription = this.patientChartStatusService
      .getPatientChartStatusSpecification(currentPatientId, action)
      .subscribe(
        (response) => {
          this.inFlightAdmitCheckPatientId = null;
          // Patient may have changed while the request was in flight.
          if (this.referralServiceProvider.PATIENT_ID !== currentPatientId) {
            return;
          }
          if (response) {
            let complianceCheck;
            let complianceStates: any = response;
            if (complianceStates["BENEFITS"] === "PARTIAL") {
              complianceStates["BENEFITS"] = "TRUE";
            }
            if (careTypeIsHospice) {
              const profileState = this.checkHospiceProfileState(
                complianceStates,
                this.patientAgeEmpty
              );
              this.handleIconsForHospice(
                complianceStates,
                profileState,
                action
              );
              // Require every expected spec key to be exactly "TRUE" and the
              // patient age to be confirmed not-empty. Missing keys, empty
              // responses, or an uninitialized patientAgeEmpty (timing race
              // on new-tab bootstrap) all fail safely to "FALSE".
              const requiredHospiceSpecs = [
                "PATIENT_CHART_STATUS",
                "PATIENT_CHART_CONTACT",
                "PATIENT_CHART_DETAILS",
                "DIAGNOSIS",
                "LEVEL_OF_CARE",
                "BENEFITS",
                "CARE_TEAM",
                "CONSENT",
              ];
              if (
                action !==
                NavBarServiceProvider.ADMIT_WITHOUT_CTI_CHECK_ACTION_NAME
              ) {
                requiredHospiceSpecs.push("CERTIFICATION");
              }
              const allSpecsTrue = requiredHospiceSpecs.every(
                (key) => complianceStates[key] === "TRUE"
              );
              complianceCheck =
                this.patientAgeEmpty === false && allSpecsTrue
                  ? "TRUE"
                  : "FALSE";
            } else {
              complianceCheck = this.checkPalliativeCompliance(
                complianceStates,
                this.eligibilityFeatureToggle
              );
            }
            if (complianceCheck === "TRUE") {
              this.referralServiceProvider.ENABLE_ADMIT = true;
            } else if (complianceStates["PATIENT_CHART_STATUS"] === "TRUE") {
              //set timer to check again
              this.referralServiceProvider.ENABLE_ADMIT = false;
            }
            this.events.publish("reloadNavBar");
          }
        },
        () => {
          this.inFlightAdmitCheckPatientId = null;
          if (this.referralServiceProvider.PATIENT_ID === currentPatientId) {
            this.referralServiceProvider.ENABLE_ADMIT = false;
          }
          console.error("Cannot access patient chart status ");
        }
      );
  }

  public hasAccessControlData() {
    return !!(this.accessControlData && this.accessControlData["domains"]);
  }

  private setAccessControlData(data: any) {
    this.accessControlData = data;
  }

  public hasAccessControl(pageResource: Enums.PAGE_RESOURCES): boolean {
    let temp = [];

    if (this.accessControlData && this.accessControlData["domains"]) {
      for (let domain of this.accessControlData["domains"]) {
        for (let resourceObj of domain["resourceAccessList"]) {
          temp.push({
            appResourceId: resourceObj["appResourceId"],
            accessLevel: resourceObj["access"],
          });
        }
      }

      let resource = temp.filter((obj) => obj.appResourceId === pageResource);
      if (resource && resource.length > 0) {
        return resource[0].accessLevel === 1 || resource[0].accessLevel === 2;
      } else {
        return false;
      }
    }
    return false;
  }

  // private setAccessControlPage(page: Enums.PAGES) {
  //     this.accessControlPage = page;
  // }

  private setAccessControlPageName(pageName: string[]) {
    this.accessControlPageName = pageName;
  }

  public hasBereavementAccess() {
    return (
      this.featureToggleOn("PDB") &&
      this.getDischargeReason() &&
      this.getDischargeReason().toLowerCase() ===
        NavBarServiceProvider.BEREAVEMENT_DISCHARGE_REASON
    );
  }

  public hasEligibilityAccess(checkForEligibility: boolean = false): boolean {
    if (checkForEligibility) {
      return (
        this.palliativeFeatureToggleOn() &&
        this.eligibilityFeatureToggleOn() &&
        this.patientIsPalliative()
      );
    }
    return this.palliativeFeatureToggleOn() && this.patientIsPalliative();
  }

  public patientIsPalliative(): boolean {
    return (
      this.getPatientChart() &&
      this.getPatientChart().careType.toLowerCase() === "palliative"
    );
  }

  private setDischargeReason(patientChartResponseData: PatientChart) {
    this.dischargeReason = patientChartResponseData.dischargedReason;
  }
  public setScriptSurePatient(
    patientChart?: PatientChart,
    forceRefresh?: boolean
  ) {
    if (patientChart && this.patientEPrescribeFeatureToggleOn()) {
      if (!this.mrnSubject || this.mrnSubject.observers.length === 0) {
        this.mrnSubject = new Subject<MRNChange>();

        const scriptSurePatient$ = this.mrnSubject.pipe(
          distinctUntilChanged(
            (previous, current) =>
              previous.mrn === current.mrn && !current.forceRefresh
          ),
          mergeMap((mrnChange) =>
            this.medicationDataService.getPatientByMRN(mrnChange.mrn)
          )
        );

        scriptSurePatient$.subscribe((scriptSurePatient) => {
          this.scriptSurePatientSubject.next(scriptSurePatient);
          this.events.publish("reloadNavBar");
        });
      }
      this.mrnSubject.next({
        mrn: patientChart.patient.mrn,
        forceRefresh: forceRefresh,
      });
    } else {
      this.scriptSurePatientSubject.next(null);
    }
  }

  private getDischargeReason() {
    return this.dischargeReason;
  }

  public setCareType(careType: string) {
    this.careType = careType;
  }

  public getCareType() {
    return this.careType;
  }

  public setAdmitDate(admitDate: string) {
    this.admitDate = admitDate ? moment(admitDate).format("YYYY-MM-DD") : null;
  }

  public getAdmitDate() {
    return this.admitDate;
  }

  private logError(method: string, args: any, errorMessage: any) {}

  private logInfo(method: string, args: any, infoMessage: any) {}

  private setHospiceFeatureToggle() {
    this.hospiceFeatureToggleChange.next(this.hospiceHouseFeatureToggleOn());
  }

  private setReportManagementFeatureToggle() {
    this.reportManagementFeatureToggleChange.next(
      this.reportManagementFeatureToggleOn()
    );
  }

  private setArSummaryFeatureToggle() {
    this.arSummaryFeatureToggleChange.next(this.arSummaryFeatureToggleOn());
  }

  private setArDetailsFeatureToggle() {
    this.arDetailsFeatureToggleChange.next(this.arDetailsFeatureToggleOn());
  }

  private setMonthlyRevenueSummaryFeatureToggle() {
    this.monthlyRevenueSummaryFeatureToggleChange.next(
      this.monthlyRevenueSummaryFeatureToggleOn()
    );
  }

  private setPreBillingReportsFeatureToggle() {
    this.preBillingReportsFeatureToggleChange.next(
      this.preBillingReportsFeatureToggleOn()
    );
  }

  private setLevelOfCareSummaryFeatureToggle() {
    this.levelOfCareSummaryFeatureToggleChange.next(
      this.levelOfCareDetailsFeatureToggleOn()
    );
  }

  private setLevelOfCareDetailsFeatureToggle() {
    this.levelOfCareDetailsFeatureToggleChange.next(
      this.levelOfCareDetailsFeatureToggleOn()
    );
  }

  private setAdverseEventFeatureToggle() {
    this.adverseEventFeatureToggleChange.next(
      this.adverseEventFeatureToggleOn()
    );
  }

  private setBenefitsFeatureToggle() {
    this.benefitsFeatureToggleChange.next(this.benefitsFeatureToggleOn());
  }

  private setEligibilityFeatureToggle() {
    this.eligibilityFeatureToggleChange.next(this.eligibilityFeatureToggleOn());
  }

  private setMonthEndCloseFeatureToggle() {
    this.monthEndCloseToggleChange.next(this.monthEndCloseFeatureToggleOn());
  }

  private setCTIV2NotRequiredToggle() {
    this.cti_notRequiredToAdmitToggleChange.next(
      this.cti_notRequiredToAdmitToggleOn()
    );
  }

  public setChartId(patientId: number) {
    this.getNewPatientChartData(patientId, false).subscribe(
      (data) => {
        let patient_data: any = data;
        if (patient_data.externalId)
          this.stateData.setChartId(patient_data.externalId);
        if (patient_data.patient && patient_data.patient.id)
          this.stateData.setPatientId(patient_data.patient.id);
      },
      (err) => {
        console.error(err);
        if (err.error && err.error.errors) {
          let error_msg = err.error.errors[0];
          this.presentToast(`${error_msg.field} : ${error_msg.error}`);
        }
      }
    );
  }

  public getScriptSurePatient(): Observable<PatientResponseDto | null> {
    return this.scriptSurePatientSubject.asObservable();
  }

  ngOnDestroy() {
    if (this.patientChartStatusSubscription) {
      this.patientChartStatusSubscription.unsubscribe();
    }
    if (this.tokenRefreshSubscription) {
      this.tokenRefreshSubscription.unsubscribe();
    }
    if (this.statusSubscription) {
      this.statusSubscription.unsubscribe();
    }
    this.events.unsubscribe("checkAdmitStatus");
    this.events.unsubscribe("admitStatusChange");
    this.events.unsubscribe("patientChartIdChange");
  }
}



// WEBPACK FOOTER //
// ./src/providers/nav-bar-service/nav-bar-service.ts